
import React, { useEffect, useState } from 'react';
import { Box, TableBody } from '@mui/material';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  CreateButton
} from './styles';
import BankUploadModal from './components/BankUploadModal';
import CustomPagination from '../../../components/CustomPagination';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import { searchBanks } from '../../../modules/masterdata/actions';
import {
  selectBanksList,
  selectSearchBanksCount,
  selectUploadBankCSVSuccess,
  selectUploadBankCSVMessage,
  selectUploadBankCSVError
} from '../../../modules/masterdata/selectors';
import {
  resetUploadBankCSVState
} from '../../../modules/masterdata/reducers';
import { IBank } from '../../../modules/masterdata/types';
import AlertPopup from '../../../components/common/AlertPopup';

const Bank: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const banks = useSelector(selectBanksList);
  const totalCount = useSelector(selectSearchBanksCount);

  const PAGE_SIZE = 20;
  const [page, setPage] = useState(1);
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const uploadSuccess = useSelector(selectUploadBankCSVSuccess);
  const uploadError = useSelector(selectUploadBankCSVError);
  const uploadMessage = useSelector(selectUploadBankCSVMessage);

  useEffect(() => {
    if (uploadSuccess) {
      setAlert({ message: uploadMessage || 'Uploaded successfully', severity: 'success' });
      dispatch(searchBanks({ search: '', page: 1, page_size: PAGE_SIZE }));
      dispatch(resetUploadBankCSVState());
      setIsModalOpen(false);
    }
    if (uploadError) {
      setAlert({ message: uploadError, severity: 'error' });
      dispatch(resetUploadBankCSVState());
    }
  }, [uploadSuccess, uploadError, uploadMessage, dispatch]);

  useEffect(() => {
    setActivePage('bank');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Banks', path: '/master-data/banks' },
    ]);
    setIsShow(true);
    dispatch(searchBanks({ search: '', page: 1, page_size: PAGE_SIZE }));
  }, [setActivePage, setPath, setIsShow, dispatch]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    dispatch(searchBanks({ search: '', page: newPage, page_size: PAGE_SIZE }));
  };

  const totalPages = Math.ceil((totalCount || 0) / PAGE_SIZE);

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">Manage Banks</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={() => setIsModalOpen(true)}>
            Upload CSV
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {banks && banks.length > 0 ? (
                banks.map((bank: IBank, index: number) => (
                  <StyledTableRow key={bank.id}>
                    <StyledTableCell>{bank.id}</StyledTableCell>
                    <StyledTableCell>{bank.bank_name}</StyledTableCell>
                    <StyledTableCell>{bank.bank_description}</StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={3} align="center">No banks found.</StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>

        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <CustomPagination
            totalPages={totalPages > 0 ? totalPages : 1}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </MainContainer>
      <BankUploadModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </Box>
  );
};

export default Bank;
